/**
 * /dashboard/careers/new  and  /dashboard/careers/:id/edit
 * Rich editor for career/job openings — mirrors BlogEditorPage pattern.
 * Uses vscode theme variables; never routes through /admin/*.
 */

import { motion } from 'framer-motion';
import { ArrowLeft, Briefcase, Check, Eye, Save, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { supabase } from '../../lib/supabase';

type CareerStatus = 'draft' | 'published' | 'closed' | 'archived';

interface CareerFormData {
  slug: string;
  title: string;
  department: string;
  location: string;
  job_type: string;
  experience_level: string;
  salary_range: string;
  hero_headline: string;
  hero_intro: string;
  description: string;
  requirements: string;
  nice_to_have: string;
  benefits: string;
  application_url: string;
  status: CareerStatus;
  closes_at: string;
  meta_title: string;
  meta_description: string;
}

const BLANK: CareerFormData = {
  slug: '', title: '', department: '', location: '', job_type: 'Full-time',
  experience_level: '', salary_range: '', hero_headline: '', hero_intro: '',
  description: '', requirements: '', nice_to_have: '', benefits: '',
  application_url: '', status: 'draft', closes_at: '', meta_title: '', meta_description: '',
};

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Extract post ID from dashboard URL: /dashboard/careers/:id/edit */
function getPostIdFromURL(): string | null {
  const m = window.location.pathname.match(/\/dashboard\/careers\/([^/]+)\/edit/);
  return m ? m[1] : null;
}

const navigateTo = (path: string) => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const inputCls = 'ds-input';
const textareaCls = 'ds-textarea';
const labelCls = 't-label block mb-1';

interface CareersEditorPageProps {
  embedded?: boolean;
  postId?: string;
}

const CareersEditorPage: React.FC<CareersEditorPageProps> = ({ embedded, postId: postIdProp }) => {
  const postId = postIdProp ?? getPostIdFromURL();
  const isEdit = !!postId;

  const [form, setForm] = useState<CareerFormData>(BLANK);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit || !postId) return;
    (async () => {
      const { data, error: err } = await supabase
        .from('career_posts')
        .select('*')
        .eq('id', postId)
        .single();
      if (err) { setError(err.message); setLoading(false); return; }
      if (data) {
        setForm({
          slug: data.slug ?? '',
          title: data.title ?? '',
          department: data.department ?? '',
          location: data.location ?? '',
          job_type: data.job_type ?? 'Full-time',
          experience_level: data.experience_level ?? '',
          salary_range: data.salary_range ?? '',
          hero_headline: data.hero_headline ?? '',
          hero_intro: data.hero_intro ?? '',
          description: data.description ?? '',
          requirements: (data.requirements ?? []).join('\n'),
          nice_to_have: (data.nice_to_have ?? []).join('\n'),
          benefits: (data.benefits ?? []).join('\n'),
          application_url: data.application_url ?? '',
          status: data.status ?? 'draft',
          closes_at: data.closes_at ? data.closes_at.slice(0, 10) : '',
          meta_title: data.meta_title ?? '',
          meta_description: data.meta_description ?? '',
        });
        setPublishedAt(data.published_at ?? null);
      }
      setLoading(false);
    })();
  }, [isEdit, postId]);

  const set = (k: keyof CareerFormData, v: string) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'title' && !isEdit) next.slug = slugify(v);
      return next;
    });
    setSaved(false);
  };

  const handleSave = async (publish = false) => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.slug.trim())  { setError('Slug is required');  return; }
    setSaving(true);
    setError(null);

    const status: CareerStatus = publish ? 'published' : form.status;
    const nowIso = new Date().toISOString();
    const shouldStampPublishedAt = status === 'published' && !publishedAt;

    const payload = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      department: form.department || null,
      location: form.location || null,
      job_type: form.job_type || null,
      experience_level: form.experience_level || null,
      salary_range: form.salary_range || null,
      hero_headline: form.hero_headline || null,
      hero_intro: form.hero_intro || null,
      description: form.description || null,
      requirements: form.requirements ? form.requirements.split('\n').map(s => s.trim()).filter(Boolean) : [],
      nice_to_have: form.nice_to_have ? form.nice_to_have.split('\n').map(s => s.trim()).filter(Boolean) : [],
      benefits: form.benefits ? form.benefits.split('\n').map(s => s.trim()).filter(Boolean) : [],
      application_url: form.application_url || null,
      status,
      closes_at: form.closes_at || null,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      ...(shouldStampPublishedAt ? { published_at: nowIso } : {}),
    };

    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      const withAudit = userId
        ? { ...payload, ...(isEdit ? { updated_by: userId } : { created_by: userId, updated_by: userId }) }
        : payload;

      if (isEdit) {
        const { error: err } = await supabase.from('career_posts').update(withAudit).eq('id', postId);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('career_posts').insert([withAudit]);
        if (err) throw err;
      }

      if (shouldStampPublishedAt) setPublishedAt(nowIso);
      setForm((f) => ({ ...f, status }));
      setSaved(true);
      if (publish) {
        setTimeout(() => navigateTo('/dashboard/careers'), 1200);
      }
    } catch (e: unknown) {
      const pgErr = e as { code?: string; message?: string; details?: string };
      if (pgErr?.code === '23505' || pgErr?.message?.toLowerCase().includes('duplicate') || pgErr?.message?.toLowerCase().includes('unique')) {
        setError('A job opening with this slug already exists. Please change the slug and try again.');
      } else {
        setError(pgErr?.message ?? 'Save failed. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="h-full ds-page flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
           style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className={embedded ? 'h-full ds-page overflow-y-auto' : 'min-h-screen ds-page'}>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 border-b px-6 py-3 flex items-center justify-between gap-3"
        style={{ background: 'var(--ds-bg-card)', borderColor: 'var(--ds-border)' }}>
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigateTo('/dashboard/careers')}
            className="ds-btn ds-btn-ghost ds-btn-sm shrink-0"
          >
            <ArrowLeft className="w-4 h-4" /> Careers
          </button>
          <span className="t-muted opacity-40">|</span>
          <div className="flex items-center gap-2 min-w-0">
            <Briefcase className="w-4 h-4 shrink-0" style={{ color: 'var(--ds-brand)' }} />
            <span className="t-card-title truncate">{form.title || 'New Opening'}</span>
            <span className={`ds-chip capitalize ${
              form.status === 'published' ? 'ds-chip-ok' :
              form.status === 'closed'    ? 'ds-chip-danger' :
              'ds-chip-warn'}`}>
              {form.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {form.status === 'published' && (
            <a href={`/careers/${form.slug}`} target="_blank" rel="noopener noreferrer"
              className="ds-btn ds-btn-secondary ds-btn-sm">
              <Eye className="w-3.5 h-3.5" /> Preview
            </a>
          )}
          <button
            type="button"
            onClick={() => void handleSave(false)}
            disabled={saving}
            className="ds-btn ds-btn-secondary ds-btn-sm disabled:opacity-50"
          >
            {saved ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save draft'}
          </button>
          <button
            type="button"
            onClick={() => void handleSave(true)}
            disabled={saving}
            className="ds-btn ds-btn-primary ds-btn-sm disabled:opacity-50"
          >
            Publish
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 ds-recessed-danger t-meta flex items-center gap-2">
          <X className="w-4 h-4 shrink-0" />{error}
          <button type="button" onClick={() => setError(null)} className="ml-auto">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Editor body */}
      <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left — main content */}
        <div className="lg:col-span-2 space-y-5">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="ds-card space-y-4">
            <h2 className="careers-editor-section-title">Job Details</h2>

            <div>
              <label className={labelCls}>Job Title *</label>
              <input value={form.title} onChange={(e) => set('title', e.target.value)}
                placeholder="e.g. Senior Backend Engineer" className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Slug *</label>
              <input value={form.slug} onChange={(e) => set('slug', slugify(e.target.value))}
                placeholder="auto-generated from title" className={inputCls}
              />
              <p className="t-meta mt-1">Public URL: /careers/{form.slug || '…'}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Department</label>
                <input value={form.department} onChange={(e) => set('department', e.target.value)}
                  placeholder="Engineering, Marketing…" className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Location</label>
                <input value={form.location} onChange={(e) => set('location', e.target.value)}
                  placeholder="Remote, Bengaluru, Hybrid…" className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Job Type</label>
                <select value={form.job_type} onChange={(e) => set('job_type', e.target.value)} className="ds-select">
                  {['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'].map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Experience Level</label>
                <select value={form.experience_level} onChange={(e) => set('experience_level', e.target.value)} className="ds-select">
                  <option value="">Select…</option>
                  {['Fresher', 'Junior (0–2 yrs)', 'Mid (2–5 yrs)', 'Senior (5–8 yrs)', 'Lead (8+ yrs)'].map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Salary Range (optional)</label>
              <input value={form.salary_range} onChange={(e) => set('salary_range', e.target.value)}
                placeholder="e.g. ₹8–12 LPA or Competitive" className={inputCls}
              />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="ds-card space-y-4">
            <h2 className="careers-editor-section-title">Listing Card</h2>
            <div>
              <label className={labelCls}>Hero Headline</label>
              <input value={form.hero_headline} onChange={(e) => set('hero_headline', e.target.value)}
                placeholder="Bold tagline shown on the card" className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Short Intro (shown on listing card)</label>
              <textarea rows={3} value={form.hero_intro} onChange={(e) => set('hero_intro', e.target.value)}
                placeholder="1–2 sentences describing the role…" className={textareaCls} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="ds-card space-y-4">
            <h2 className="careers-editor-section-title">Full Job Description</h2>
            <textarea rows={12} value={form.description} onChange={(e) => set('description', e.target.value)}
              placeholder="Full JD — supports Markdown…" className={`${textareaCls} font-mono`} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="ds-card space-y-4">
            <h2 className="careers-editor-section-title">Requirements & Benefits</h2>
            <div>
              <label className={labelCls}>Must-Have Requirements (one per line)</label>
              <textarea rows={5} value={form.requirements} onChange={(e) => set('requirements', e.target.value)}
                placeholder={"5+ years Python experience\nStrong system design skills\nExperience with Supabase"} className={textareaCls} />
            </div>
            <div>
              <label className={labelCls}>Nice-to-Have (one per line)</label>
              <textarea rows={3} value={form.nice_to_have} onChange={(e) => set('nice_to_have', e.target.value)}
                placeholder={"Open-source contributions\nStartup experience"} className={textareaCls} />
            </div>
            <div>
              <label className={labelCls}>Benefits (one per line)</label>
              <textarea rows={4} value={form.benefits} onChange={(e) => set('benefits', e.target.value)}
                placeholder={"Fully remote\nCompetitive salary\nHealth insurance\nLearning budget"} className={textareaCls} />
            </div>
          </motion.div>
        </div>

        {/* Right — settings sidebar */}
        <div className="space-y-5">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}
            className="ds-card space-y-4">
            <h2 className="careers-editor-section-title">Publishing</h2>
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value as CareerStatus)} className="ds-select">
                {['draft', 'published', 'closed', 'archived'].map(s => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Application Closes (optional)</label>
              <input type="date" value={form.closes_at} onChange={(e) => set('closes_at', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Application URL / Email (optional)</label>
              <input value={form.application_url} onChange={(e) => set('application_url', e.target.value)}
                placeholder="Leave blank to use the built-in apply form" className={inputCls} />
              <p className="t-meta mt-1">When empty, candidates apply at /careers/{form.slug || '…'}/apply</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
            className="ds-card space-y-4">
            <h2 className="careers-editor-section-title">SEO</h2>
            <div>
              <label className={labelCls}>Meta Title</label>
              <input value={form.meta_title} onChange={(e) => set('meta_title', e.target.value)}
                placeholder="Defaults to job title" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Meta Description</label>
              <textarea rows={3} value={form.meta_description} onChange={(e) => set('meta_description', e.target.value)}
                placeholder="Short description for search results…" className={textareaCls} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }}
            className="ds-card-sm flex flex-col gap-2">
            <button
              type="button"
              onClick={() => void handleSave(false)}
              disabled={saving}
              className="ds-btn ds-btn-secondary w-full disabled:opacity-50"
            >
              <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={() => void handleSave(true)}
              disabled={saving}
              className="ds-btn ds-btn-primary w-full disabled:opacity-50"
            >
              Publish opening
            </button>
            <button
              type="button"
              onClick={() => navigateTo('/dashboard/careers')}
              className="ds-btn ds-btn-ghost w-full"
            >
              ← Back to list
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CareersEditorPage;
